/* eslint-disable */
import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, IconButton, Table, TableHead, TableBody,
  TableRow, TableCell, Button, Tooltip, Skeleton,
} from '@mui/material';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ConfirmDialog from 'Shared/ConfirmDialog';
import Toast from '../../../Shared/UI/Toast';
import useNetworkLoader from 'hooks/useNetworkLoader';
import ValidateCartModal from './ValidateCartModal';
import DeleteCartModal from './DeleteCartModal';
import ConfigContext from 'Shared/ConfigContext';
import { validatedCart, removePanier } from 'Shared/ConfigContext/Actions';
import toast from 'Utils/toast';
import formatErrorMessage from 'config/errorMessages';
import { deleteProductCart, getCartDetail, deleteCart as deleteCartApi } from '../../../Services';
import validateCart from '../services';
import { getProductsData } from 'Components/Catalog/CatalogServices';
import noImage from 'Images/no_image.svg';
import deleteIcon from 'Images/delete_icon.svg';

const FONT = "'Open Sans', sans-serif";

const STATUS_CONFIG = {
  'Validé':   { color: '#0AD69F', bg: '#0AD69F1D', border: '#0AD69F' },
  'En Cours': { color: '#F79F1F', bg: '#FFF3E0',   border: '#F79F1F' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color: '#8398AE', bg: '#F1F5F9', border: '#8398AE' };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      border: `1px solid ${cfg.border}`, borderRadius: '20px', px: '10px', py: '3px',
      backgroundColor: cfg.bg,
    }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: cfg.color }} />
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: cfg.color, fontFamily: FONT }}>
        {status}
      </Typography>
    </Box>
  );
};

const TH = ({ children, width }) => (
  <TableCell sx={{
    fontFamily: FONT, fontSize: '14px', fontWeight: 600, color: '#385576',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    borderBottom: '1px solid #DEE5EE', py: '6px', px: '16px',
    backgroundColor: '#fff', width,
  }}>
    {children}
  </TableCell>
);

const TD = ({ children, sx = {} }) => (
  <TableCell sx={{ fontFamily: FONT, fontSize: '14px', color: '#1A2B3C', borderBottom: '1px solid #F1F5F9', py: '8px', px: '16px', ...sx }}>
    {children}
  </TableCell>
);

const DetailSkeleton = () => (
  <Box sx={{ backgroundColor: '#F8FAFC', minHeight: '100vh', pt: '90px', px: '40px', pb: '40px', mt: '-80px', fontFamily: FONT }}>
    <Skeleton variant="text" width={180} height={20} sx={{ mb: '8px' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '20px' }}>
      <Skeleton variant="text" width={260} height={28} />
      <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '20px' }} />
    </Box>
    <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #DEE5EE', overflow: 'hidden', p: '16px' }}>
        {[...Array(4)].map((_, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '16px' }}>
            <Skeleton variant="rectangular" width={39} height={39} sx={{ borderRadius: '6px', flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="40%" height={20} />
              <Skeleton variant="text" width="25%" height={16} />
            </Box>
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton variant="text" width={40} height={20} />
            <Skeleton variant="text" width={90} height={20} />
          </Box>
        ))}
      </Box>
      <Box sx={{ width: '432px', flexShrink: 0, backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #DEE5EE', p: '14px 20px' }}>
        <Skeleton variant="text" width={180} height={18} sx={{ mb: '4px' }} />
        <Skeleton variant="text" width={140} height={32} />
      </Box>
    </Box>
  </Box>
);

const CartDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { callApi } = useNetworkLoader();
  const { dispatch, state: { expeditions } } = useContext(ConfigContext);

  const [cart, setCart] = useState(null);
  const [cartLoading, setCartLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [removedIds, setRemovedIds] = useState([]);
  const [productToast, setProductToast] = useState({ open: false, message: '', severity: 'success' });
  const [showValidateDialog, setShowValidateDialog] = useState(false);
  const [validateLoading, setValidateLoading] = useState(false);
  const [validateError, setValidateError] = useState(null);
  const [showDeleteCartDialog, setShowDeleteCartDialog] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [deletingCart, setDeletingCart] = useState(false);

  useEffect(() => {
    callApi(
      () => getCartDetail(id),
      (data) => {
        setCart(data);
        setQuantities(Object.fromEntries((data.products || []).map((p) => [p.id, p.quantity])));
        setCartLoading(false);
        const noImageRefs = (data.products || [])
          .filter((p) => !p.image)
          .map((p) => p.reference);
        if (noImageRefs.length > 0) {
          getProductsData('page=0&size=200', { references: noImageRefs })
            .then((res) => {
              const imageMap = {};
              (res?.content || []).forEach((p) => { imageMap[p.reference] = p.image; });
              setCart((prev) => ({
                ...prev,
                products: (prev.products || []).map((p) => ({
                  ...p,
                  image: p.image || imageMap[p.reference] || null,
                })),
              }));
            })
            .catch(() => {});
        }
      },
      false,
    );
  }, [id]);

  if (cartLoading) return <DetailSkeleton />;

  const products = (cart.products || []).filter((p) => !removedIds.includes(p.id));
  const isValidated = cart.validated === 1;

  const handleQuantityChange = (productId, delta) => {
    setQuantities((prev) => {
      const next = Math.max(1, (prev[productId] || 1) + delta);
      return { ...prev, [productId]: next };
    });
  };

  const totalAmount = products.reduce((sum, p) => {
    const remise = (p.remise || 0) + (p.remiseExceptionnelle || 0);
    return sum + (p.price || 0) * (quantities[p.id] ?? p.quantity) * (1 - remise / 100);
  }, 0);

  return (
    <Box sx={{ backgroundColor: '#F8FAFC', minHeight: '100vh', pt: '90px', px: '40px', pb: '40px', mt: '-80px', fontFamily: FONT, '& *': { fontFamily: FONT } }}>

      {/* Back + Header */}
      <Box sx={{ mb: '20px', mt: '4px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Box
            onClick={() => history.goBack()}
            sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', mb: '8px' }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 11, color: '#8397AE' }} />
            <Typography sx={{ fontSize: '14px', color: '#8397AE', fontFamily: FONT, fontWeight: 600 }}>
              Retour à la liste des paniers
            </Typography>
          </Box>

          {/* Row 2: title + badge */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#385576', fontFamily: FONT }}>
              Détails du panier:{' '}
              <Box component="span" sx={{ fontWeight: 700, color: '#000000' }}>{cart.name}</Box>
            </Typography>
            <StatusBadge status={cart.statut || (isValidated ? 'Validé' : 'En Cours')} />
          </Box>
        </Box>

        {!isValidated && (
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Button
                onClick={() => setShowDeleteCartDialog(true)}
                startIcon={<CloseIcon />}
                sx={{
                  backgroundColor: '#EF456E',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontFamily: FONT,
                  borderRadius: '8px',
                  px: '32px',
                  py: '8px',
                  '& .MuiButton-startIcon svg': { fontSize: '16px', mr: '-6px' },
                  '&:hover': { backgroundColor: '#EF456E' },
                }}
              >
                Supprimer le panier
              </Button>
              <Button
                onClick={() => setShowValidateDialog(true)}
                disabled={products.length === 0}
                startIcon={<CheckIcon />}
                sx={{
                  backgroundColor: '#0AD69F',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontFamily: FONT,
                  borderRadius: '8px',
                  px: '32px',
                  py: '8px',
                  '& .MuiButton-startIcon svg': { fontSize: '16px', mr: '-4px' },
                  '&:hover': { backgroundColor: '#0AD69F' },
                  '&.Mui-disabled': { backgroundColor: '#0AD69F', color: '#fff', opacity: 0.35 },
                }}
              >
                Valider le panier
              </Button>
            </Box>
          )}
        </Box>

      {/* Main content + right card */}
      <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

        {/* Table */}
        <Box sx={{ flex: 1, minWidth: 0, backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #DEE5EE', overflow: 'hidden', boxShadow: '0px 4px 16px rgba(0,0,0,0.06)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TH width="37%">Produits</TH>
                <TH>Prix Unitaire Brut TTC</TH>
                <TH>Quantité</TH>
                <TH>Montant TTC</TH>
                {!isValidated && <TH>Actions</TH>}
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id} sx={{ '&:hover': { backgroundColor: '#FAFBFC' } }}>
                  <TD>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Box sx={{
                        width: 39, height: 39, borderRadius: '6px',
                        backgroundColor: '#F8FAFC',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        overflow: 'hidden',
                      }}>
                        <img
                          src={product.image || noImage}
                          alt=""
                          style={{ maxWidth: 35, maxHeight: 35, objectFit: 'contain' }}
                          onError={({ currentTarget }) => {
                            // eslint-disable-next-line no-param-reassign
                            currentTarget.onerror = null;
                            // eslint-disable-next-line no-param-reassign
                            currentTarget.src = noImage;
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', fontFamily: FONT }}>
                          {product.name}
                        </Typography>
                        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#8397AE', fontFamily: FONT, mt: '-4px' }}>
                          RÉF: {product.reference}
                        </Typography>
                      </Box>
                    </Box>
                  </TD>
                  <TD>
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', fontFamily: FONT }}>
                      {product.price.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </Typography>
                  </TD>
                  <TD>
                    {isValidated ? (
                      <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', fontFamily: FONT }}>
                        {String(product.quantity).padStart(2, '0')}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #DEE5EE', borderRadius: '6px', overflow: 'hidden', width: '84px', height: '28px' }}>
                        <Box
                          onClick={() => handleQuantityChange(product.id, -1)}
                          sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#8398AE', fontSize: '18px', userSelect: 'none', '&:hover': { backgroundColor: '#F1F5F9', color: '#007BC6' } }}
                        >
                          <span style={{ fontWeight: 700, lineHeight: 1 }}>−</span>
                        </Box>
                        <Box sx={{ width: '1.5px', height: '30px', backgroundColor: '#DEE5EE' }} />
                        <Box
                          component="input"
                          type="number"
                          value={quantities[product.id] ?? product.quantity}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            if (!isNaN(val) && val >= 1) setQuantities((prev) => ({ ...prev, [product.id]: val }));
                            else if (e.target.value === '') setQuantities((prev) => ({ ...prev, [product.id]: '' }));
                          }}
                          onBlur={() => {
                            if (!quantities[product.id] || quantities[product.id] < 1) setQuantities((prev) => ({ ...prev, [product.id]: 1 }));
                          }}
                          sx={{
                            width: '32px', height: '28px',
                            border: 'none', outline: 'none',
                            textAlign: 'center',
                            fontSize: '14px', fontWeight: 700, color: '#1A2B3C',
                            background: 'transparent', fontFamily: FONT,
                            '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                            MozAppearance: 'textfield',
                          }}
                        />
                        <Box sx={{ width: '1.5px', height: '30px', backgroundColor: '#DEE5EE' }} />
                        <Box
                          onClick={() => handleQuantityChange(product.id, 1)}
                          sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#8398AE', fontSize: '18px', userSelect: 'none', '&:hover': { backgroundColor: '#F1F5F9', color: '#007BC6' } }}
                        >
                          <span style={{ fontWeight: 700, lineHeight: 1 }}>+</span>
                        </Box>
                      </Box>
                    )}
                  </TD>
                  <TD>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#007BC6', fontFamily: FONT }}>
                      {(product.price * (quantities[product.id] ?? product.quantity) * (1 - ((product.remise || 0) + (product.remiseExceptionnelle || 0)) / 100)).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </Typography>
                  </TD>
                  {!isValidated && (
                    <TD>
                      <Tooltip title="Supprimer le produit" placement="top" arrow>
                        <IconButton
                          onClick={() => setDeleteProductId(product.id)}
                          sx={{ color: '#EF456E', p: '6px' }}
                        >
                          <img src={deleteIcon} alt="delete" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                        </IconButton>
                      </Tooltip>
                    </TD>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Right total card */}
        <Box sx={{
          width: '432px', flexShrink: 0,
          backgroundColor: '#fff', borderRadius: '10px',
          border: '1px solid #DEE5EE', p: '14px 20px',
          position: 'sticky', top: '100px',
          boxShadow: '0px 4px 16px rgba(0,0,0,0.06)',
        }}>
          <Typography sx={{ fontSize: '13px', color: '#8398AE', fontFamily: FONT, mb: '-2px' }}>
            Montant Total TTC ({products.length} référence{products.length > 1 ? 's' : ''})
          </Typography>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#007BC6', fontFamily: FONT }}>
            {totalAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} Dh
          </Typography>
        </Box>
      </Box>

      {/* Delete product dialog */}
      <ConfirmDialog
        open={Boolean(deleteProductId)}
        variant="delete"
        title="Supprimer ce produit"
        message="Cette action supprimera définitivement ce produit du panier. Souhaitez-vous continuer ?"
        onClose={() => setDeleteProductId(null)}
        loading={deletingProduct}
        onConfirm={() => {
          if (!deleteProductId) return;
          setDeletingProduct(true);
          callApi(
            () => deleteProductCart(cart.id, [deleteProductId]),
            () => {
              setRemovedIds((prev) => [...prev, deleteProductId]);
              setDeleteProductId(null);
              setDeletingProduct(false);
              setProductToast({ open: true, message: 'Produit supprimé du panier', severity: 'success' });
            },
            true,
            () => { setDeletingProduct(false); },
          );
        }}
      />

      <ValidateCartModal
        open={showValidateDialog}
        onClose={() => { setShowValidateDialog(false); setValidateError(null); }}
        expeditions={expeditions}
        loading={validateLoading}
        error={validateError}
        onConfirm={(expedition, comment) => {
          setValidateError(null);
          const body = {
            expeditionId: expedition.id,
            commercialId: null,
            comment,
            name: cart.name,
            products: (cart.products || []).map((p) => ({
              quantity: quantities[p.id] ?? p.quantity,
              reference: p.reference,
              isExist: true,
            })),
          };
          setValidateLoading(true);
          callApi(
            () => validateCart(cart.id, body),
            () => {
              dispatch(validatedCart(cart.name));
              setValidateLoading(false);
              setShowValidateDialog(false);
              history.push('/cart');
            },
            true,
            (e) => {
              setValidateLoading(false);
              const msg = e?.response?.data?.statusLabel;
              if (msg) setValidateError(formatErrorMessage(msg));
            },
          );
        }}
      />

      <DeleteCartModal
        open={showDeleteCartDialog}
        onClose={() => setShowDeleteCartDialog(false)}
        loading={deletingCart}
        onConfirm={() => {
          setDeletingCart(true);
          callApi(
            () => deleteCartApi(cart.id),
            () => {
              dispatch(removePanier(cart.name));
              history.push('/cart');
            },
            true,
            () => { setDeletingCart(false); },
          );
        }}
      />

      <Toast
        open={productToast.open}
        message={productToast.message}
        severity={productToast.severity}
        onClose={() => setProductToast((t) => ({ ...t, open: false }))}
      />
    </Box>
  );
};

export default CartDetail;
